/**
 * #2022 — per-project onsite workflow requirements.
 *
 * A project may require any combination of: placing an Order, taking a full
 * Inventory (qty-on-hand) count, and/or scanning Out-of-Stock shelf tags. The PWA
 * shows only the required step(s) for the active task's project.
 */

export interface WorkflowSteps {
  order: boolean;
  inventory: boolean;
  oos: boolean;
}

type TaskLike =
  | {
      requires_order?: boolean | null;
      requires_inventory?: boolean | null;
      requires_oos?: boolean | null;
    }
  | null
  | undefined;

/**
 * Which onsite steps the task's project requires — the flags are authoritative,
 * with NO order-only fallback. A project with no flags set shows no capture
 * step at all (a valid configuration: a project that takes no order, inventory,
 * or OOS). Existing projects are backfilled to requires_order=true so they keep
 * order entry; new projects choose explicitly. (#2022)
 */
export function workflowStepsForTask(task: TaskLike): WorkflowSteps {
  return {
    order: !!task?.requires_order,
    inventory: !!task?.requires_inventory,
    oos: !!task?.requires_oos,
  };
}

/** The three #2022 requirement flags, normalized to booleans. */
export interface TaskWorkflowFlags {
  requires_order: boolean;
  requires_inventory: boolean;
  requires_oos: boolean;
}

interface PendingTaskFlagsLike {
  project_id: number;
  task_id: number;
  requires_order?: boolean | null;
  requires_inventory?: boolean | null;
  requires_oos?: boolean | null;
}

/**
 * Recover the authoritative workflow flags for the active task from the
 * pending-tasks payload (the only source that carries them).
 *
 * Why this exists: the auto-start-on-check-in path builds the active task from
 * navigation params that omit the #2022 flags, so `currentTask` starts flagless
 * and the order gate wrongly falls back to "order" (the #2022 Phase-2 live bug).
 * Matching is by `project_id` — the flags are PROJECT-level, so a synthetic
 * `task_id === 0` (nav-back / project-only check-in) still resolves correctly.
 *
 * Returns null when there is no active task, no matching pending task, or the
 * matched payload carries no flags at all (offline / a pre-#2022 record) — in
 * which case the caller should leave `currentTask` untouched and let
 * `workflowStepsForTask` apply its order-only fallback.
 */
export function findWorkflowFlagsForTask(
  task: { project_id: number; task_id?: number } | null | undefined,
  pendingTasks: ReadonlyArray<PendingTaskFlagsLike>
): TaskWorkflowFlags | null {
  if (!task) return null;
  const byExact = pendingTasks.find(
    (t) => t.project_id === task.project_id && t.task_id === task.task_id
  );
  const match = byExact ?? pendingTasks.find((t) => t.project_id === task.project_id);
  if (!match) return null;
  // Only authoritative if the payload actually carried flags (the server sends
  // explicit booleans; absence means an older/offline payload we shouldn't trust).
  if (
    match.requires_order == null &&
    match.requires_inventory == null &&
    match.requires_oos == null
  ) {
    return null;
  }
  return {
    requires_order: !!match.requires_order,
    requires_inventory: !!match.requires_inventory,
    requires_oos: !!match.requires_oos,
  };
}

/** Per-project required-attachment flags (#2077), normalized to booleans. */
export interface TaskRequiredAttachments {
  requiresPhotos: boolean;
  requiresDocuments: boolean;
}

interface PendingTaskAttachmentLike {
  project_id: number;
  task_id: number;
  requires_photos?: boolean | null;
  requires_documents?: boolean | null;
}

/**
 * Recover the #2077 required-attachment flags for the active task from the
 * pending-tasks payload, matched by project_id (same approach as
 * findWorkflowFlagsForTask — the flags are PROJECT-level).
 *
 * Returns all-false when there is no active task or no matching pending task.
 * A missing match means "nothing required" — the safe default, so absent /
 * offline data can never trap a rep behind an attachment they can't satisfy.
 */
export function requiredAttachmentsForTask(
  task: { project_id: number; task_id?: number } | null | undefined,
  pendingTasks: ReadonlyArray<PendingTaskAttachmentLike>
): TaskRequiredAttachments {
  if (!task) return { requiresPhotos: false, requiresDocuments: false };
  const byExact = pendingTasks.find((t) => t.project_id === task.project_id && t.task_id === task.task_id);
  const match = byExact ?? pendingTasks.find((t) => t.project_id === task.project_id);
  return {
    requiresPhotos: !!match?.requires_photos,
    requiresDocuments: !!match?.requires_documents,
  };
}

interface PendingTaskSignatureLike {
  project_id: number;
  task_id: number;
  requires_signature?: boolean | null;
}

/**
 * #2092 — is a checkout signature required for the active task's project?
 * Matched by project_id like the other flag helpers.
 *
 * REQUIRED BY DEFAULT — note the inverted default vs. requiredAttachmentsForTask:
 * an attachment requirement defaults OFF so missing data can't trap a rep, but
 * the signature gate defaults ON, because the policy is "no leaving without a
 * signature" and a stale cache / older payload must not quietly reopen the skip
 * path. Only an explicit requires_signature === false opts a project out.
 */
export function signatureRequiredForTask(
  task: { project_id: number; task_id?: number } | null | undefined,
  pendingTasks: ReadonlyArray<PendingTaskSignatureLike>
): boolean {
  if (!task) return true;
  const byExact = pendingTasks.find((t) => t.project_id === task.project_id && t.task_id === task.task_id);
  const match = byExact ?? pendingTasks.find((t) => t.project_id === task.project_id);
  return match?.requires_signature !== false;
}

interface PendingTaskTeamLike {
  project_id: number;
  task_id: number;
  is_team_project?: boolean | null;
  is_current_user_lead?: boolean | null;
}

/**
 * #2951 — on a TEAM project, only the team LEAD completes the shared project
 * deliverables (survey, photos, documents). A rep who is on a team project but
 * is NOT the lead has NO required deliverables — they just check in (GPS) and
 * record their time. Everything stays required for the lead.
 *
 * Lead status is authoritative from the SET assignment (associateid = lead;
 * rep1/rep2 = the other members), surfaced on the task as `is_current_user_lead`;
 * `is_team_project` = the set has rep1/rep2. NOT the self-declared checkout toggle.
 *
 * Defaults to REQUIRED (true) when we can't tell (no task, no match, or missing
 * flags — e.g. a CALL task, an offline/older payload) so nothing ever wrongly
 * excuses a lead's or a solo rep's deliverables. Only an explicit "team project
 * AND not the current user's lead" suppresses them.
 */
export function sharedDeliverablesRequiredForTask(
  task: ({ project_id: number; task_id?: number } & Partial<PendingTaskTeamLike>) | null | undefined,
  pendingTasks: ReadonlyArray<PendingTaskTeamLike>
): boolean {
  if (!task) return true;
  // The ACTIVE task usually carries its own flags, and asking it first removes a
  // race that blocked real reps (#3524): `pendingTasks` is fetched in the
  // background and starts empty, so a non-lead who tapped Finish before it
  // landed found no match, fell through to the required-by-default rule, and was
  // gated on a survey the lead owns. Intermittent by nature — it depended on
  // whether they were faster than the request.
  if (task.is_team_project && task.is_current_user_lead === false) return false;
  const byExact = pendingTasks.find((t) => t.project_id === task.project_id && t.task_id === task.task_id);
  const match = byExact ?? pendingTasks.find((t) => t.project_id === task.project_id);
  if (!match) return true;
  if (match.is_team_project && match.is_current_user_lead === false) return false;
  return true;
}

/**
 * #2959 — the authoritative team config for a task, taken from the SET
 * assignment (associateid = lead; rep1/rep2 = members) as surfaced on the
 * pending-tasks payload — NEVER the self-declared checkout toggle. Used to drive
 * the checkout's read-only "Team Project" indicator and to gate the manager
 * signature to the lead.
 *
 * Returns `{ isTeamProject: true, isCurrentUserLead }` when the task matches a
 * team set, else `null` (a solo set, a call, or an offline/older payload with
 * no flags). A `null` result means "treat as not a team project" — the caller
 * falls back to the normal, always-required signature rule.
 */
export function teamConfigForTask(
  task:
    | { project_id: number; task_id?: number; is_team_project?: boolean; is_current_user_lead?: boolean }
    | null
    | undefined,
  pendingTasks: ReadonlyArray<PendingTaskTeamLike>
): { isTeamProject: true; isCurrentUserLead: boolean } | null {
  if (!task) return null;
  // #3022 — prefer the task's OWN captured flags. Completed/worked tasks carry
  // the deterministic team config from their IDB session, so this resolves even
  // after the set drops out of the pending-tasks payload (completed) or a reload
  // — no dependency on the set still being pending.
  if (task.is_team_project) {
    return { isTeamProject: true, isCurrentUserLead: task.is_current_user_lead === true };
  }
  const byExact = pendingTasks.find((t) => t.project_id === task.project_id && t.task_id === task.task_id);
  const match = byExact ?? pendingTasks.find((t) => t.project_id === task.project_id);
  if (!match || !match.is_team_project) return null;
  return { isTeamProject: true, isCurrentUserLead: match.is_current_user_lead === true };
}
